import { FaBook, FaChevronDown, FaChevronUp, FaFilePdf, FaDownload, FaSearch } from "react-icons/fa";
import { useState } from "react";

export default function Documents() {
  const [expandedSection, setExpandedSection] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const bylawsSections = [
    {
      id: "article1",
      title: "Article 1: Definitions",
      content: {
        intro: "In these Staff Governance By-Laws, the following definitions apply:",
        definitions: [
          { term: "NYUAD Staff", definition: "Academic Staff, Administrative Staff, and Researchers." },
          { term: "Academic Staff", definition: "All staff coded as 'academic support staff' in Workday." },
          { term: "Administrative Staff", definition: "All staff employees of NYUAD excluding Academic Staff, Researchers and postdoctoral associates." },
          { term: "Researchers", definition: "All researcher staff employees of NYUAD, excluding postdoctoral associates." },
          { term: "Administrative Unit", definition: "For the purpose of these guidelines, administrative unit status will be assigned to the 5 largest administrative divisions (Provost, ABO, Campus Life, Research Institute, External Relations)." },
          { term: "Unit Representatives", definition: "Individuals elected to represent their administrative unit. Each Unit Representative shall be a member of the Administrative Staff of the Administrative Unit which they represent." },
          { term: "At-Large Administrative Members", definition: "Two individuals elected from the Administrative Staff body at large without direct unit representation." },
          { term: "Representatives", definition: "Unit Representatives & At-Large Administrative Members" },
          { term: "Staff Council Members", definition: "The Representatives and the Chairs and Vice Chairs of the Academic Staff and Researcher Committees." },
          { term: "Senior Administration", definition: "The Vice Chancellor, the Provost, Chief of Staff, Chief Administrative and Business Officer, Vice Provosts and the executive staff of the Office of the Vice-Chancellor, Office of the Provost, Office of the CABO, Dean of Students and other high-level members of NYUAD staff." },
          { term: "Officer Role", definition: "Chair, Vice Chair, Governance Liaison Officer, Global Network Officer and DEI Officer." },
          { term: "Cross Council Committee (CCC)", definition: "A university committee which comprises representatives from the various constituencies i.e., faculty, staff, students (undergraduate and graduate), postdoctoral associates, researchers, and contract staff." },
          { term: "SCSC", definition: "The Staff Council Steering Committee. Serves as the main channel between the Senior Administration and the Staff Council and oversees the strategic trajectory of Staff Council." }
        ]
      }
    },
    {
      id: "article2",
      title: "Article 2: Officer Roles of Staff Council",
      content: {
        intro: "The following Officers shall be elected from and by the Representatives:",
        officers: [
          {
            role: "Chair",
            note: "Should ordinarily have prior experience as the Vice Chair",
            responsibilities: [
              "Setting the overall agenda of the Staff Council and SCSC",
              "Preparing agendas for Staff Council meetings, SCSC meetings, and Staff Forums",
              "Collaborating with Chief of Staff in preparation of All Staff Meeting agendas",
              "Co-Chairing All Staff Meetings with Chief of Staff",
              "Chairing SCSC, Staff Council meetings, and Staff Forums",
              "Representing the SCSC and Staff Council in meetings with staff, faculty, students, and leadership",
              "Adhering to these By-Laws and ensuring all decisions are carried into effect",
              "Fostering a spirit of shared governance, collaborative work and inclusion"
            ]
          },
          {
            role: "Vice-Chair",
            responsibilities: [
              "Supporting the Chair in fulfilling all responsibilities",
              "Chairing meetings in the Chair's absence",
              "Serving as second representative on CCC along with Governance Liaison Officer"
            ]
          },
          {
            role: "Governance Liaison Officer",
            responsibilities: [
              "Serving as primary liaison to Faculty Council, Postdoctoral Council, and Student Government",
              "Representing Staff Council on the CCC and Faculty Council",
              "Reporting back to Staff Council on CCC and Faculty Council meetings",
              "Ensuring Staff Council representation at meetings by nominating alternate SCSC members when unable to attend"
            ]
          },
          {
            role: "Global Network Officer",
            responsibilities: [
              "Liaising with 'NYU Abu Dhabi (NYC) representatives' on the New York University Administrative Management Council (AMC)",
              "Ensuring Abu Dhabi staff concerns are represented in AMC discussions",
              "Informing Staff Council of topics and decisions from AMC meetings",
              "Representing NYUAD NYC staff interests in Staff Council discussions",
              "Facilitating communication throughout the NYU Global Network",
              "Liaising with Faculty Committee on NYU's Global Network"
            ]
          },
          {
            role: "Diversity, Equity & Inclusion (DEI) Officer",
            responsibilities: [
              "Chairing Staff Council committees or working groups specialized in DEI issues",
              "Ensuring communication between DEI groups and SCSC",
              "Liaising with Office of Inclusion and Equity",
              "Pursuing collaborations around DEI issues and interests"
            ]
          },
          {
            role: "Past Chair Ex Officio",
            note: "Upon majority vote of SCSC, may remain on Staff Council and SCSC for one additional year in non-voting capacity"
          }
        ]
      }
    },
    {
      id: "article3",
      title: "Article 3: Staff Council Steering Committee (SCSC)",
      content: {
        overview: "The SCSC serves as the main channel between the Senior Administration and the Staff Council.",
        composition: "Comprises those Representatives who hold Officer Roles, along with the Chair of the Researcher Committee and Chair of the Academic Staff Committee.",
        functions: [
          "Represent Staff Council in meetings with Senior Administration and community",
          "Execute the recommendations of Staff Council",
          "Represent Staff Council in fora, events, committees as needed",
          "Initiate studies, formulate responsive actions and make recommendations",
          "Propose and establish standing or ad hoc committees as necessary",
          "Oversee elections for Representatives and staff representation on University committees",
          "Make appointments for staff representation on University and ad hoc committees",
          "Provide reports to Staff Council Members at each meeting",
          "Consider recommendations from committees and formulate recommendations to Staff Council"
        ]
      }
    },
    {
      id: "article4",
      title: "Article 4: Academic Staff Committee",
      content: {
        purpose: "Voice the interests and concerns of the Academic Staff constituency and advise Staff Council and Senior Administration on relevant matters.",
        composition: [
          "1 representative from Arts and Humanities Division",
          "1 representative from Social Science Division",
          "1 representative from Science Division",
          "1 representative from Engineering Division",
          "1 representative from Academic Librarian body",
          "1 representative from Center for Writing",
          "2 At-Large representatives elected from Academic Staff"
        ],
        leadership: "Committee elects Chair and Vice Chair who serve on SCSC (Chair) and represent committee interests."
      }
    },
    {
      id: "article5",
      title: "Article 5: Researcher Committee",
      content: {
        purpose: "Voice the interests and concerns of the Researchers constituency and advise Staff Council and Senior Administration on relevant matters.",
        composition: [
          "1 representative from Arts and Humanities Division",
          "1 representative from Social Science Division",
          "1 representative from Science Division",
          "1 representative from Engineering Division",
          "1 representative from Research Centers",
          "2 At-Large representatives elected from Researchers"
        ],
        leadership: "Committee elects Chair and Vice Chair who serve on SCSC (Chair) and represent committee interests."
      }
    },
    {
      id: "article7",
      title: "Article 7: Elections and Terms of Service",
      content: {
        terms: [
          {
            position: "Representatives & Committee Members",
            term: "2 years",
            limit: "Maximum of 2 consecutive terms (4 years)"
          },
          {
            position: "Officers (Chair, Vice Chair, etc.)",
            term: "2 years",
            limit: "Maximum of 2 consecutive terms in same Officer Role"
          }
        ],
        elections: [
          "Elections held annually in Spring semester",
          "Approximately half of Representatives and Members elected each year",
          "Voters may not vote in more than one constituency",
          "Run-off elections if no candidate receives majority (>50%)",
          "Officer elections held at first Staff Council meeting after general elections"
        ]
      }
    },
    {
      id: "article8",
      title: "Article 8: Duties and Responsibilities",
      content: {
        representatives: [
          "Attend Staff Council meetings and participate in discussions",
          "Gather feedback from Unit members on issues being discussed",
          "Disseminate information to their Unit about discussions and decisions",
          "Vote at Staff Council meetings on behalf of their Unit",
          "Fill an Officer Role or sit on at least one Staff Council Committee"
        ],
        expectations: [
          "All Representatives and Members expected to attend meetings and participate in shared governance",
          "Must communicate any limitations or inability to meet expectations with SCSC",
          "Members absent from three regularly scheduled meetings may be considered for removal by vote"
        ]
      }
    },
    {
      id: "article9",
      title: "Article 9: Confidentiality",
      content: "While recognizing the significance of transparency, details of committee and council discussions – especially involving personnel matters or actions of individuals – should be considered confidential unless otherwise noted. This does not prevent elected representatives from regularly informing constituencies about general committee discussions and decisions reached."
    },
    {
      id: "article10",
      title: "Article 10: Amendments/Rules of Order",
      content: {
        amendments: "These By-Laws may be amended by two-thirds vote of Members present at Staff Council meeting, provided:",
        requirements: [
          "Quorum has been met",
          "Written notice of proposed amendment circulated to all Members at least one week prior",
          "Opinions of NYUAD Staff have been polled"
        ],
        rulesOfOrder: "Procedures shall be governed by Roberts Rules of Order, Newly Revised, 11th Edition."
      }
    },
    {
      id: "appendix",
      title: "Appendix: Voting Procedures",
      content: {
        purpose: "Explain processes for conducting elections and referenda.",
        process: [
          {
            step: "Notice of Position",
            details: "Role, responsibilities, commitments, meeting frequency, eligibility criteria, voting parties, deadlines, and Call for Nominations distributed"
          },
          {
            step: "Nominations & Candidacy",
            details: "Nominations via email or electronic forms. Self-nominated candidates confirmed. Others contacted for willingness to serve. Candidate statements due within 3 business days."
          },
          {
            step: "Setting Up a Vote",
            details: "Conducted via secure anonymous platform over 3-5 business days. Ballots include position, candidates, statements, and voting options."
          },
          {
            step: "Conducting a Vote",
            details: "Administered by Voting Administrator (VA) who must maintain confidentiality and may not have vested interest in outcome."
          },
          {
            step: "Reporting Results",
            details: "VA reports to SCSC, Office of Vice Chancellor, relevant committees. Candidates informed prior to general announcement. Results reported within 3 business days."
          }
        ]
      }
    }
  ];

  const filteredSections = bylawsSections.filter(
    section =>
      section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      JSON.stringify(section.content).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div
      style={{
        padding: "40px",
        maxWidth: "1400px",
        margin: "0 auto",
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: "40px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "12px" }}>
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "28px",
            }}
          >
            <FaBook />
          </div>
          <div>
            <h1
              style={{
                fontSize: "36px",
                fontWeight: "700",
                color: "#2d3748",
                margin: 0,
              }}
            >
              Staff Governance By-Laws
            </h1>
            <p style={{ color: "#718096", fontSize: "16px", margin: "4px 0 0 0" }}>
              New York University Abu Dhabi • Effective Date: 2021
            </p>
          </div>
        </div>

        {/* Download Button */}
        <div style={{ display: "flex", gap: "12px", marginTop: "20px" }}>
          <button
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: "#e53e3e",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              fontSize: "15px",
              fontWeight: "600",
              cursor: "pointer",
              boxShadow: "0 4px 6px rgba(229, 62, 62, 0.3)",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#c53030")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#e53e3e")}
          >
            <FaFilePdf />
            Download Full PDF
          </button>
        </div>

        {/* Search Bar */}
        <div style={{ marginTop: "24px", position: "relative" }}>
          <FaSearch
            style={{
              position: "absolute",
              left: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#a0aec0",
              fontSize: "16px",
            }}
          />
          <input
            type="text"
            placeholder="Search by-laws content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "14px 16px 14px 48px",
              fontSize: "15px",
              border: "2px solid #e2e8f0",
              borderRadius: "10px",
              outline: "none",
              transition: "border-color 0.2s",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#667eea")}
            onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
          />
        </div>
      </div>

      {/* Preamble */}
      <div
        style={{
          background: "linear-gradient(135deg, #667eea10 0%, #764ba210 100%)",
          border: "1px solid #667eea40",
          borderRadius: "12px",
          padding: "24px",
          marginBottom: "30px",
        }}
      >
        <p style={{ fontSize: "15px", lineHeight: "1.7", color: "#2d3748", margin: 0 }}>
          <strong>Note:</strong> If any part of these Guidelines is inconsistent with NYU policies as set
          forth in the most recent versions of the Bylaws of NYU, or any other controlling NYU documents,
          the NYU policies will supersede those contained herein.
        </p>
        <p style={{ fontSize: "15px", lineHeight: "1.7", color: "#2d3748", marginTop: "12px", marginBottom: 0 }}>
          The NYU Abu Dhabi Staff Council engages with academic, administrative, and research staff to
          understand their needs and concerns. The Staff Council Steering Committee serves as the main
          channel of communication between the NYUAD staff and (i) Staff Council, and (ii) NYUAD leadership.
          The decisions and recommendations of Staff Council and the Staff Council Steering Committee are
          advisory to the NYUAD leadership.
        </p>
      </div>

      {/* Table of Contents */}
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          padding: "24px",
          marginBottom: "30px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e2e8f0",
        }}
      >
        <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#2d3748", marginBottom: "16px" }}>
          Table of Contents
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "12px" }}>
          {bylawsSections.map((section) => (
            <button
              key={section.id}
              onClick={() => toggleSection(section.id)}
              style={{
                padding: "12px 16px",
                background: expandedSection === section.id ? "#667eea15" : "#f7fafc",
                border: "1px solid",
                borderColor: expandedSection === section.id ? "#667eea" : "#e2e8f0",
                borderRadius: "8px",
                textAlign: "left",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: expandedSection === section.id ? "600" : "500",
                color: expandedSection === section.id ? "#667eea" : "#4a5568",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#667eea";
                e.currentTarget.style.background = "#667eea10";
              }}
              onMouseLeave={(e) => {
                if (expandedSection !== section.id) {
                  e.currentTarget.style.borderColor = "#e2e8f0";
                  e.currentTarget.style.background = "#f7fafc";
                }
              }}
            >
              {section.title}
            </button>
          ))}
        </div>
      </div>

      {/* Sections */}
      {filteredSections.map((section) => (
        <div
          key={section.id}
          style={{
            background: "white",
            borderRadius: "12px",
            marginBottom: "20px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          }}
        >
          <button
            onClick={() => toggleSection(section.id)}
            style={{
              width: "100%",
              padding: "24px",
              background: expandedSection === section.id ? "#667eea" : "white",
              color: expandedSection === section.id ? "white" : "#2d3748",
              border: "none",
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "20px",
              fontWeight: "700",
              transition: "all 0.2s",
            }}
          >
            {section.title}
            {expandedSection === section.id ? <FaChevronUp /> : <FaChevronDown />}
          </button>

          {expandedSection === section.id && (
            <div style={{ padding: "24px", borderTop: "1px solid #e2e8f0" }}>
              {/* Article 1: Definitions */}
              {section.id === "article1" && (
                <div>
                  <p style={{ fontSize: "15px", lineHeight: "1.7", color: "#4a5568", marginBottom: "20px" }}>
                    {section.content.intro}
                  </p>
                  <div style={{ display: "grid", gap: "16px" }}>
                    {section.content.definitions.map((def, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: "16px",
                          background: "#f7fafc",
                          borderRadius: "8px",
                          borderLeft: "4px solid #667eea",
                        }}
                      >
                        <strong style={{ color: "#667eea", fontSize: "15px" }}>{def.term}:</strong>
                        <span style={{ color: "#4a5568", fontSize: "15px", marginLeft: "8px" }}>
                          {def.definition}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Article 2: Officer Roles */}
              {section.id === "article2" && (
                <div>
                  <p style={{ fontSize: "15px", lineHeight: "1.7", color: "#4a5568", marginBottom: "24px" }}>
                    {section.content.intro}
                  </p>
                  {section.content.officers.map((officer, idx) => (
                    <div
                      key={idx}
                      style={{
                        marginBottom: "24px",
                        padding: "20px",
                        background: "#f7fafc",
                        borderRadius: "10px",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <h3 style={{ fontSize: "18px", fontWeight: "700", color: "#667eea", marginBottom: "8px" }}>
                        {officer.role}
                      </h3>
                      {officer.note && (
                        <p style={{ fontSize: "14px", color: "#718096", fontStyle: "italic", marginBottom: "12px" }}>
                          {officer.note}
                        </p>
                      )}
                      {officer.responsibilities && (
                        <ul style={{ marginLeft: "20px", color: "#4a5568", fontSize: "15px", lineHeight: "1.8" }}>
                          {officer.responsibilities.map((resp, rIdx) => (
                            <li key={rIdx}>{resp}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Article 3: SCSC */}
              {section.id === "article3" && (
                <div>
                  <div style={{ marginBottom: "20px" }}>
                    <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#2d3748", marginBottom: "8px" }}>
                      Overview
                    </h3>
                    <p style={{ fontSize: "15px", color: "#4a5568", lineHeight: "1.7" }}>
                      {section.content.overview}
                    </p>
                  </div>
                  <div style={{ marginBottom: "20px" }}>
                    <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#2d3748", marginBottom: "8px" }}>
                      Composition
                    </h3>
                    <p style={{ fontSize: "15px", color: "#4a5568", lineHeight: "1.7" }}>
                      {section.content.composition}
                    </p>
                  </div>
                  <div>
                    <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#2d3748", marginBottom: "12px" }}>
                      Functions
                    </h3>
                    <ul style={{ marginLeft: "20px", color: "#4a5568", fontSize: "15px", lineHeight: "1.8" }}>
                      {section.content.functions.map((func, idx) => (
                        <li key={idx}>{func}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Article 4 & 5: Committees */}
              {(section.id === "article4" || section.id === "article5") && (
                <div>
                  <div style={{ marginBottom: "20px" }}>
                    <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#2d3748", marginBottom: "8px" }}>
                      Purpose
                    </h3>
                    <p style={{ fontSize: "15px", color: "#4a5568", lineHeight: "1.7" }}>
                      {section.content.purpose}
                    </p>
                  </div>
                  <div style={{ marginBottom: "20px" }}>
                    <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#2d3748", marginBottom: "12px" }}>
                      Composition
                    </h3>
                    <ul style={{ marginLeft: "20px", color: "#4a5568", fontSize: "15px", lineHeight: "1.8" }}>
                      {section.content.composition.map((member, idx) => (
                        <li key={idx}>{member}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#2d3748", marginBottom: "8px" }}>
                      Leadership
                    </h3>
                    <p style={{ fontSize: "15px", color: "#4a5568", lineHeight: "1.7" }}>
                      {section.content.leadership}
                    </p>
                  </div>
                </div>
              )}

              {/* Article 7: Elections */}
              {section.id === "article7" && (
                <div>
                  <div style={{ marginBottom: "24px" }}>
                    <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#2d3748", marginBottom: "12px" }}>
                      Terms of Service
                    </h3>
                    <div style={{ display: "grid", gap: "12px" }}>
                      {section.content.terms.map((term, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: "16px",
                            background: "#f7fafc",
                            borderRadius: "8px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div>
                            <strong style={{ color: "#2d3748", fontSize: "15px" }}>{term.position}</strong>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ color: "#667eea", fontWeight: "600", fontSize: "15px" }}>{term.term}</div>
                            <div style={{ color: "#718096", fontSize: "13px" }}>{term.limit}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#2d3748", marginBottom: "12px" }}>
                      Election Procedures
                    </h3>
                    <ul style={{ marginLeft: "20px", color: "#4a5568", fontSize: "15px", lineHeight: "1.8" }}>
                      {section.content.elections.map((proc, idx) => (
                        <li key={idx}>{proc}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Article 8: Duties */}
              {section.id === "article8" && (
                <div>
                  <div style={{ marginBottom: "24px" }}>
                    <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#2d3748", marginBottom: "12px" }}>
                      Representative Responsibilities
                    </h3>
                    <ul style={{ marginLeft: "20px", color: "#4a5568", fontSize: "15px", lineHeight: "1.8" }}>
                      {section.content.representatives.map((resp, idx) => (
                        <li key={idx}>{resp}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#2d3748", marginBottom: "12px" }}>
                      Expectations & Accountability
                    </h3>
                    <ul style={{ marginLeft: "20px", color: "#4a5568", fontSize: "15px", lineHeight: "1.8" }}>
                      {section.content.expectations.map((exp, idx) => (
                        <li key={idx}>{exp}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Article 9: Confidentiality */}
              {section.id === "article9" && (
                <p style={{ fontSize: "15px", lineHeight: "1.7", color: "#4a5568" }}>
                  {section.content}
                </p>
              )}

              {/* Article 10: Amendments */}
              {section.id === "article10" && (
                <div>
                  <div style={{ marginBottom: "20px" }}>
                    <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#2d3748", marginBottom: "8px" }}>
                      Amendment Process
                    </h3>
                    <p style={{ fontSize: "15px", color: "#4a5568", lineHeight: "1.7", marginBottom: "12px" }}>
                      {section.content.amendments}
                    </p>
                    <ul style={{ marginLeft: "20px", color: "#4a5568", fontSize: "15px", lineHeight: "1.8" }}>
                      {section.content.requirements.map((req, idx) => (
                        <li key={idx}>{req}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#2d3748", marginBottom: "8px" }}>
                      Rules of Order
                    </h3>
                    <p style={{ fontSize: "15px", color: "#4a5568", lineHeight: "1.7" }}>
                      {section.content.rulesOfOrder}
                    </p>
                  </div>
                </div>
              )}

              {/* Appendix: Voting */}
              {section.id === "appendix" && (
                <div>
                  <p style={{ fontSize: "15px", lineHeight: "1.7", color: "#4a5568", marginBottom: "24px" }}>
                    <strong>Purpose:</strong> {section.content.purpose}
                  </p>
                  {section.content.process.map((proc, idx) => (
                    <div
                      key={idx}
                      style={{
                        marginBottom: "20px",
                        padding: "20px",
                        background: "#f7fafc",
                        borderRadius: "10px",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#667eea", marginBottom: "8px" }}>
                        Step {idx + 1}: {proc.step}
                      </h3>
                      <p style={{ fontSize: "15px", color: "#4a5568", lineHeight: "1.7", margin: 0 }}>
                        {proc.details}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {filteredSections.length === 0 && (
        <div
          style={{
            padding: "60px",
            textAlign: "center",
            background: "white",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
          }}
        >
          <p style={{ color: "#718096", fontSize: "16px" }}>
            No sections match your search. Try different keywords.
          </p>
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          marginTop: "40px",
          padding: "24px",
          background: "#f7fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          textAlign: "center",
        }}
      >
        <p style={{ fontSize: "14px", color: "#718096", margin: 0 }}>
          <strong>Supersedes:</strong> Staff Governance Guidelines (Adopted 2019)
        </p>
        <p style={{ fontSize: "14px", color: "#718096", margin: "8px 0 0 0" }}>
          جامعة نيويورك أبوظبي • NYU Abu Dhabi
        </p>
      </div>
    </div>
  );
}
